// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * RFC 4180 CSV writer with a formula-injection guard.
 *
 * `carry-ledger.csv` will be opened in Excel — that is what it is for. Any field starting `=`, `+`,
 * `-`, `@`, tab or CR is executed as a formula on open in Excel and Google Sheets, and a tool name
 * or a label legitimately can start with `-`. The guard is a leading apostrophe, the OWASP-standard
 * mitigation; LibreOffice renders it literally, which is a cosmetic regression we accept over a
 * live formula in someone's cost report.
 *
 * Control characters become visible escapes so ONE ROW IS ALWAYS ONE PHYSICAL LINE. A CSV whose
 * row count differs from its line count cannot be `wc -l`'d or `grep`'d, which is most of what
 * anyone actually does with one. Note that every control character below is written as an escape
 * sequence rather than as a literal byte — a literal one in the source survives copy-paste and
 * tooling only by luck.
 */

const DANGEROUS_LEAD = /^[=+\-@\t\r]/;
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/g;
const BOM = '\ufeff';

function escapeField(value: string): string {
  let v = value
    .replace(/\\/g, '\\\\')
    .replace(/\r/g, '\\r')
    .replace(/\n/g, '\\n')
    .replace(/\t/g, '\\t')
    .replace(CONTROL_CHARS, (c) => `\\x${c.charCodeAt(0).toString(16).padStart(2, '0')}`);

  const dangerous = DANGEROUS_LEAD.test(value);
  if (dangerous) v = `'${v}`;

  // `;` forces quoting for de/fr/es Excel locales, which use it as the separator. A field that got
  // the apostrophe guard is ALWAYS quoted too: unquoted, some importers strip the apostrophe and
  // put the formula back.
  const needsQuote = dangerous || /[",;\n\r]/.test(v) || v !== v.trim();
  return needsQuote ? `"${v.replace(/"/g, '""')}"` : v;
}

/** Serialise to a CSV string with a UTF-8 BOM and CRLF line endings, as Excel expects. */
export function toCsv(headers: readonly string[], rows: readonly (readonly string[])[]): string {
  const lines = [headers.map(escapeField).join(',')];
  for (const row of rows) lines.push(row.map(escapeField).join(','));
  return `${BOM}${lines.join('\r\n')}\r\n`;
}

/**
 * Escape text for the self-contained HTML report.
 *
 * The report quotes tool names and file paths that came out of someone else's session log, and it
 * gets opened from `file://`. Nothing goes into that document as markup.
 */
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
